import { Fragment, type ReactNode } from 'react';
import { ASSISTANT_LINK_TYPES, type AssistantLinkType } from '@contracts/assistant';
import type { Id } from '@contracts/common';

/**
 * The safe Markdown subset Hana writes in (spec §6.2, D127): paragraphs, line breaks, `-`/`*` and `1.` lists,
 * `**bold**`, `*italic*` / `_italic_`, `` `code` ``, and object links `[label](obj:type:uuid)`. A hand parser, no
 * library and no `dangerouslySetInnerHTML`: everything else, raw HTML and ordinary links included, is a text node that
 * React escapes. Only closed marks match, so a half-typed `**Ha` in the live bubble stays text until its `**` arrives.
 * An object link of an unknown type or with an id that is not a uuid is its label as plain text: the reader never sees
 * the raw `[…](obj:…)` markdown.
 *
 * S5 D39: a choice chip `[X](reply:X)` offers an answer the person sends with one click. D44: its text is its label
 * (both trimmed and NFC), so the person sends exactly what they read; it must be short plain text (`REPLY_TEXT_MAX`,
 * no link, no `obj:`), and a message holds at most `REPLY_MAX` of them. Anything else, and every chip where the caller
 * gives no `renderReply` (a briefing, a nudge, an older message), is its label as text.
 */

export type ObjLink = { label: string; type: AssistantLinkType; id: Id };
export type ReplyChip = { label: string; text: string };

export const REPLY_TEXT_MAX = 200;
export const REPLY_MAX = 12;

type Token =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'strong'; children: Token[] }
  | { kind: 'em'; children: Token[] }
  | { kind: 'obj'; link: ObjLink }
  | { kind: 'reply'; chip: ReplyChip };

/**
 * Any closed `[label](obj:word:id)`, its label holding at most one `[…]` pair of its own (`[[Khẩn] Gọi khách]`);
 * whether it is a chip is decided after the match (`objOrText`).
 */
const OBJ = String.raw`\[((?:[^\[\]\n]|\[[^\[\]\n]*\]){1,120})\]\(obj:(\w{1,32}):([^\s()]{1,64})\)`;
/** The shape the resolve route accepts (zod's `z.uuid()`): anything else would fail every chip of the message. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const LINK_TYPES: ReadonlySet<string> = new Set(ASSISTANT_LINK_TYPES);

/**
 * Any closed `[label](reply:text)`, the text holding at most one `(…)` pair of its own (`Dự án Cầu Rồng (2026)`), both
 * matched past the limit, so an over-long one is caught (and shown as its label) rather than left as raw markdown.
 */
const REPLY = String.raw`\[((?:[^\[\]\n]|\[[^\[\]\n]*\]){1,400})\]\(reply:((?:[^\n()]|\([^\n()]*\)){0,400})\)`;
/**
 * A link, a page address, another link scheme or markdown in the text, or an invisible or bidi format character
 * (`\p{Cf}`: a zero-width space, U+202E…) that would make the chip read other than it sends: no plain text.
 */
const NOT_PLAIN = /obj:|reply:|:\/\/|www\.|mailto:|javascript:|[<>`[\]*]|\p{Cf}/iu;

const same = (x: string): string => x.trim().normalize('NFC');

function replyOrText(label: string, text: string): Token {
  const t = same(text);
  return t !== '' && t === same(label) && [...t].length <= REPLY_TEXT_MAX && !NOT_PLAIN.test(t)
    ? { kind: 'reply', chip: { label: t, text: t } }
    : { kind: 'text', text: label };
}

function objOrText(label: string, type: string, id: string): Token {
  return LINK_TYPES.has(type) && UUID.test(id)
    ? { kind: 'obj', link: { label, type: type as AssistantLinkType, id } }
    : { kind: 'text', text: label };
}
// Left to right, the first mark that closes wins: code (nothing inside it is formatted), bold, italic, an object link.
const INLINE = new RegExp(
  String.raw`\x60([^\x60\n]+)\x60|\*\*([^*\n]+?)\*\*|\*([^*\s][^*\n]*?)\*|(?<![\p{L}\p{N}_])_([^_\n]+?)_(?![\p{L}\p{N}_])|${OBJ}|${REPLY}`,
  'gu',
);

function inline(text: string): Token[] {
  const out: Token[] = [];
  let at = 0;
  for (const m of text.matchAll(INLINE)) {
    if (m.index > at) out.push({ kind: 'text', text: text.slice(at, m.index) });
    at = m.index + m[0].length;
    if (m[1] !== undefined) out.push({ kind: 'code', text: m[1] });
    else if (m[2] !== undefined) out.push({ kind: 'strong', children: inline(m[2]) });
    else if (m[3] !== undefined) out.push({ kind: 'em', children: inline(m[3]) });
    else if (m[4] !== undefined) out.push({ kind: 'em', children: inline(m[4]) });
    else if (m[5] !== undefined) out.push(objOrText(m[5], m[6]!, m[7]!));
    else out.push(replyOrText(m[8]!, m[9]!));
  }
  if (at < text.length) out.push({ kind: 'text', text: text.slice(at) });
  return out;
}

type Block =
  | { kind: 'p'; lines: Token[][] }
  | { kind: 'ul' | 'ol'; items: Token[][] };

const UL = /^[-*] +/;
const OL = /^\d+\. +/;

const kindOf = (line: string): Block['kind'] => (UL.test(line) ? 'ul' : OL.test(line) ? 'ol' : 'p');

/** Blank lines split blocks; inside one, a run of `- ` or `1. ` lines is a list and the other runs are paragraphs. */
function blocks(text: string): Block[] {
  const out: Block[] = [];
  const push = (kind: Block['kind'], lines: string[]) => {
    if (kind === 'p') out.push({ kind, lines: lines.map(inline) });
    else out.push({ kind, items: lines.map((l) => inline(l.replace(kind === 'ul' ? UL : OL, ''))) });
  };
  for (const chunk of text.replace(/\r\n?/g, '\n').split(/\n[ \t]*\n/)) {
    let kind: Block['kind'] | null = null;
    let lines: string[] = [];
    for (const line of chunk.split('\n')) {
      if (line.trim() === '') continue;
      const next = kindOf(line);
      if (kind !== null && next !== kind) { push(kind, lines); lines = []; }
      kind = next;
      lines.push(line);
    }
    if (kind !== null) push(kind, lines);
  }
  return out;
}

/** D39: past the first `REPLY_MAX` chips of a message, a chip is its label as text. */
function capReplies(bs: Block[]): Block[] {
  let n = 0;
  const cap = (tokens: Token[]): Token[] => tokens.map((t) => {
    if (t.kind === 'reply') { n += 1; return n <= REPLY_MAX ? t : { kind: 'text', text: t.chip.label }; }
    if (t.kind === 'strong' || t.kind === 'em') return { ...t, children: cap(t.children) };
    return t;
  });
  return bs.map((b) => (b.kind === 'p' ? { ...b, lines: b.lines.map(cap) } : { ...b, items: b.items.map(cap) }));
}

function collect(tokens: Token[], out: ObjLink[]): void {
  for (const t of tokens) {
    if (t.kind === 'obj') out.push(t.link);
    else if (t.kind === 'strong' || t.kind === 'em') collect(t.children, out);
  }
}

/** The object links of a message, in order (duplicates kept; the resolver de-duplicates). */
export function parseObjLinks(text: string): ObjLink[] {
  const out: ObjLink[] = [];
  for (const b of blocks(text)) for (const line of b.kind === 'p' ? b.lines : b.items) collect(line, out);
  return out;
}

type Render = { renderObject: (o: ObjLink) => ReactNode; renderReply?: ((r: ReplyChip) => ReactNode) | undefined };

function Inline({ tokens, r }: { tokens: Token[]; r: Render }) {
  return tokens.map((t, i) => {
    switch (t.kind) {
      case 'text': return <Fragment key={i}>{t.text}</Fragment>;
      case 'code':
        return <code key={i} className="rounded-[4px] bg-[hsl(var(--h-muted))] px-1 font-mono text-[0.92em]">{t.text}</code>;
      case 'strong': return <strong key={i} className="font-bold"><Inline tokens={t.children} r={r} /></strong>;
      case 'em': return <em key={i}><Inline tokens={t.children} r={r} /></em>;
      case 'obj': return <Fragment key={i}>{r.renderObject(t.link)}</Fragment>;
      case 'reply': return <Fragment key={i}>{r.renderReply === undefined ? t.chip.label : r.renderReply(t.chip)}</Fragment>;
    }
  });
}

/** A line of choice chips and nothing else: shown as a wrapping row of chips, as the starter chips are. */
const chipsOnly = (tokens: Token[]): boolean => tokens.some((t) => t.kind === 'reply')
  && tokens.every((t) => t.kind === 'reply' || (t.kind === 'text' && t.text.trim() === ''));

/** F14: the text's last line is a row of choice chips only, so a caret after it would sit on an empty line of its own. */
export function endsWithChipRow(text: string): boolean {
  const last = capReplies(blocks(text)).at(-1);
  return last?.kind === 'p' && chipsOnly(last.lines.at(-1)!);
}

export function Markdown({ text, renderObject, renderReply }: {
  text: string; renderObject: (o: ObjLink) => ReactNode;
  /** D39: how a choice chip shows; without it every chip is its label as text. */
  renderReply?: (r: ReplyChip) => ReactNode;
}) {
  const r: Render = { renderObject, renderReply };
  const row = (line: Token[]) => renderReply !== undefined && chipsOnly(line);
  return (
    <>
      {capReplies(blocks(text)).map((b, i) => {
        if (b.kind === 'p') {
          return (
            <p key={i} className="whitespace-pre-wrap">
              {b.lines.map((line, j) => (
                <Fragment key={j}>
                  {/* A chip row is a box of its own: no line break next to it (it would show as an empty line). */}
                  {j > 0 && !(row(line) || row(b.lines[j - 1]!)) && <br />}
                  {row(line)
                    ? <span data-reply-row="" className="my-0.5 flex flex-wrap gap-1.5 whitespace-normal">
                      <Inline tokens={line.filter((t) => t.kind === 'reply')} r={r} />
                    </span>
                    // F12: an older line of options only reads as a list, not one run of words.
                    : renderReply === undefined && chipsOnly(line)
                      ? line.flatMap((t) => (t.kind === 'reply' ? [t.chip.label] : [])).join(' · ')
                      : <Inline tokens={line} r={r} />}
                </Fragment>
              ))}
            </p>
          );
        }
        const List = b.kind;
        return (
          <List key={i} className={b.kind === 'ul' ? 'list-disc pl-5' : 'list-decimal pl-5'}>
            {b.items.map((item, j) => <li key={j}><Inline tokens={item} r={r} /></li>)}
          </List>
        );
      })}
    </>
  );
}
